import { Maybe } from '@zaptic-external/ts-monads'
import * as LaunchDarkly from 'launchdarkly-js-client-sdk'
import { useEffect, useState } from 'react'
import type { Flags, UserContext } from '../../../shared/cfg/flags.no-deps'
import { isNil } from '../../../shared/lib/functional/nil.no-deps'

let mLaunchDarklyClient: Maybe<LaunchDarkly.LDClient> = Maybe.nothing()

export async function initialize(userContext?: Omit<UserContext, 'featureFlags'>): Promise<LaunchDarkly.LDClient> {
    // fallback value is a prod client id, it will be used only if client does not have new env values in local storage yet
    // so it's for backward compatibility. We might remove it some time after release of feature flag system.
    const launchDarklyClientSideId = window.ENV.launchDarklyClientSideId ?? '623c3cb1e358581460b4dcad'

    const ldUserContext = isNil(userContext)
        ? {
              anonymous: true,
              appVersion: window.ENV.releaseVersion,
              isFromServer: false,
              ...(window.ENV.isTest ? { key: 'test-user-anonymous' } : {}),
          }
        : {
              key: window.ENV.isTest ? 'test-user' : userContext.email,
              name: userContext.name,
              customerSlug: userContext.customerSlug,
              email: userContext.email,
              language: userContext.language,
              isZapticStaff: userContext.isZapticStaff,
              appVersion: window.ENV.releaseVersion,
              isFromServer: false,
          }
    const client = LaunchDarkly.initialize(
        launchDarklyClientSideId,
        { ...ldUserContext, kind: 'user' },
        {
            bootstrap: 'localStorage',
            logger: LaunchDarkly.basicLogger({
                level: 'warn',
            }),
        }
    )
    await client.waitForInitialization()
    mLaunchDarklyClient = Maybe.just(client)
    return client
}

export function getFlagValue<T extends keyof Flags>(flag: T, defaultValue: Flags[T]): Flags[T] {
    if (mLaunchDarklyClient.isJust()) {
        return mLaunchDarklyClient.value.variation(flag, defaultValue)
    }
    return defaultValue
}

export function onFlagChange<T extends keyof Flags>(
    flag: T,
    callback: (newValue: Flags[T], previousValue: Flags[T]) => void
): void {
    if (mLaunchDarklyClient.isJust()) {
        mLaunchDarklyClient.value.on(`change:${flag}`, callback)
    }
}

export function useFlagValue<T extends keyof Flags>(flag: T, defaultValue: Flags[T]): Flags[T] {
    const [value, setValue] = useState(defaultValue)
    useEffect(() => {
        setValue(getFlagValue(flag, defaultValue))
    }, [flag, defaultValue])
    return value
}
